import { Box } from '@mui/material'
import {
  dateHeureLisible,
  estEngagement,
  format,
  money,
  PAYMENT_METHOD_LABELS,
  referenceAffichable,
  soldeDocument,
  type CurrencyCode,
  type StoreMembership,
  type Transaction,
} from '@iobiz/shared'

/**
 * Document imprimable d'une vente : facture ou ticket.
 *
 * ⭐ Présenté en TABLEAU. Une suite de lignes empilées se lit mal dès qu'il y a
 * plus de trois articles, et surtout elle ne peut pas porter la TVA : un
 * justificatif doit montrer, colonne par colonne, ce qui compose le total —
 * quantité, prix unitaire hors taxes, taux, montant hors taxes, montant TTC.
 *
 * ⭐ Les coordonnées viennent de la SESSION, pas d'un appel réseau : un
 * document doit s'imprimer pendant une coupure, et un entête vide ne serait pas
 * présentable au client.
 *
 * ⚠️ Invisible à l'écran, seul visible sur papier. La règle d'impression vit
 * dans la page qui l'affiche : on masque toute l'application et on ne laisse
 * que ce bloc.
 */

const ENCRE = '#000'

function Cellule({
  children,
  droite,
  gras,
  entete,
}: {
  children?: React.ReactNode
  droite?: boolean
  gras?: boolean
  entete?: boolean
}): JSX.Element {
  return (
    <Box
      component={entete ? 'th' : 'td'}
      sx={{
        textAlign: droite ? 'right' : 'left',
        fontWeight: gras || entete ? 700 : 400,
        padding: '3px 6px',
        borderBottom: entete ? `1px solid ${ENCRE}` : '1px dotted #999',
        whiteSpace: droite ? 'nowrap' : 'normal',
        verticalAlign: 'top',
      }}
    >
      {children}
    </Box>
  )
}

function Total({
  libelle,
  valeur,
  fort,
}: {
  libelle: string
  valeur: string
  fort?: boolean
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 4,
        fontWeight: fort ? 700 : 400,
        fontSize: fort ? '13px' : '11px',
        borderTop: fort ? `1px solid ${ENCRE}` : 'none',
        paddingTop: fort ? '4px' : 0,
        marginTop: fort ? '4px' : 0,
      }}
    >
      <span>{libelle}</span>
      <span>{valeur}</span>
    </Box>
  )
}

export function FactureImprimable({
  vente,
  boutique,
  rendu,
}: {
  vente: Transaction
  boutique: StoreMembership | undefined
  /** Monnaie rendue, quand le document suit un encaissement en espèces. */
  rendu?: number
}): JSX.Element {
  const devise = (boutique?.currencyCode ?? vente.currencyCode ?? 'XAF') as CurrencyCode
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  /**
   * ⚠️ Le titre suit le TYPE avant le numéro. Un devis n'a pas de numéro légal :
   * la règle précédente en concluait « TICKET » et sortait une proposition de
   * prix sous les traits d'un justificatif d'achat. Une COMMANDE non plus
   * n'est pas un justificatif d'achat — c'est un engagement à honorer.
   */
  const titre =
    vente.docType === 'quote'
      ? 'DEVIS'
      : vente.docType === 'order'
        ? 'BON DE COMMANDE'
        : vente.docType === 'credit_note'
          ? 'AVOIR'
          : vente.number
            ? 'FACTURE'
            : 'TICKET'
  const engagement = estEngagement(vente.docType)

  return (
    <Box
      id="ticket"
      sx={{
        // Hors écran plutôt que `display: none` : un bloc masqué ne s'imprime
        // pas du tout, alors qu'un bloc déporté reste imprimable.
        position: 'absolute',
        left: '-200vw',
        top: 0,
        width: '100%',
        maxWidth: '190mm',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        fontSize: '11px',
        lineHeight: 1.4,
        color: ENCRE,
        background: '#fff',
        // ⚠️ Tout en NOIR, sans exception. Les couleurs du thème — libellés en
        // bleu, quantités en ambre — s'invitaient dans le document imprimé :
        // sur une imprimante monochrome elles ressortent en gris pâle, et un
        // justificatif à moitié lisible n'est pas un justificatif.
        '& *': { color: ENCRE },
      }}
    >
      {/* ------------------------------------------------------------ entête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 6, mb: 3 }}>
        <Box>
          <Box sx={{ fontSize: '15px', fontWeight: 700 }}>
            {boutique?.legalName || boutique?.name || 'Boutique'}
          </Box>
          {boutique?.address && <Box>{boutique.address}</Box>}
          {boutique?.phone && <Box>Tél. {boutique.phone}</Box>}
          {boutique?.taxId && <Box>N° fiscal {boutique.taxId}</Box>}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Box sx={{ fontSize: '15px', fontWeight: 700, letterSpacing: '.05em' }}>{titre}</Box>
          <Box sx={{ fontWeight: 700 }}>{referenceAffichable(vente)}</Box>
          <Box>{dateHeureLisible(vente.createdAt)}</Box>
          {vente.cashierName && (
            <Box>
              {vente.docType === 'quote'
                ? 'Établi par '
                : vente.docType === 'order'
                  ? 'Établie par '
                  : 'Encaissé par '}
              {vente.cashierName}
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3, paddingTop: '4px', borderTop: `2px solid ${ENCRE}` }}>
        <strong>Client : </strong>
        {vente.customerName ?? 'Comptoir'}
      </Box>

      {/* ------------------------------------------------------------- lignes */}
      <Box
        component="table"
        sx={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}
      >
        <Box component="thead">
          <Box component="tr">
            <Cellule entete>Désignation</Cellule>
            <Cellule entete droite>Qté</Cellule>
            <Cellule entete droite>P.U. HT</Cellule>
            <Cellule entete droite>TVA</Cellule>
            <Cellule entete droite>Total HT</Cellule>
            <Cellule entete droite>Total TTC</Cellule>
          </Box>
        </Box>
        <Box component="tbody">
          {vente.lines.map((l) => (
            <Box component="tr" key={l.id}>
              <Cellule>{l.label}</Cellule>
              <Cellule droite>{l.quantity}</Cellule>
              <Cellule droite>{prix(l.unitPrice)}</Cellule>
              {/* ⚠️ Le taux FIGÉ dans la ligne, jamais celui de l'article
                  aujourd'hui : une facture émise ne se recalcule pas. */}
              <Cellule droite>{l.taxRate > 0 ? `${l.taxRate} %` : '—'}</Cellule>
              <Cellule droite>{prix(l.subtotal)}</Cellule>
              <Cellule droite gras>
                {prix(l.total)}
              </Cellule>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ------------------------------------------------------------ totaux */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ minWidth: '70mm' }}>
          <Total libelle="Total HT" valeur={prix(vente.subtotalAmount)} />
          {vente.discountAmount > 0 && (
            <Total libelle="Remise" valeur={`- ${prix(vente.discountAmount)}`} />
          )}
          {vente.taxBreakdown.length === 0 ? (
            <Total libelle="TVA" valeur={prix(0)} />
          ) : (
            vente.taxBreakdown.map((t) => (
              <Total key={t.rate} libelle={`TVA ${t.rate} %`} valeur={prix(t.amount)} />
            ))
          )}
          <Total libelle="TOTAL TTC" valeur={prix(vente.totalAmount)} fort />
          {vente.payments.map((p) => (
            <Total key={p.id} libelle={PAYMENT_METHOD_LABELS[p.method]} valeur={prix(p.amount)} />
          ))}
          {rendu !== undefined && rendu > 0 && (
            <Total libelle="Rendu monnaie" valeur={prix(rendu)} />
          )}
          {/* ⚠️ Jamais sur un DEVIS ni une COMMANDE : rien n'est dû tant qu'ils
              n'ont pas été transformés en facture. Remis avec « RESTE DÛ » en
              gras, l'un comme l'autre se liraient comme un document impayé. */}
          {/* ⭐ Dans les deux sens — voir `TicketCaisse`. Une facture qui tait
              un trop-perçu fait réclamer deux fois la même somme. */}
          {!engagement && soldeDocument(vente) !== 0 && (
            <Total
              libelle={soldeDocument(vente) > 0 ? 'RESTE DÛ' : 'AVOIR CLIENT'}
              valeur={prix(Math.abs(soldeDocument(vente)))}
              fort
            />
          )}
        </Box>
      </Box>

      {!vente.number && (
        <Box sx={{ marginTop: '10px', paddingTop: '4px', borderTop: `1px solid ${ENCRE}` }}>
          {/* ⚠️ Le client doit savoir que ce numéro n'est pas le définitif : il
              sert à retrouver le document, mais celui-ci en portera un autre
              une fois la connexion revenue. Vaut pour les trois types, tous
              numérotés dans la même suite continue. */}
          Référence provisoire —{' '}
          {vente.docType === 'quote'
            ? 'devis établi'
            : vente.docType === 'order'
              ? 'commande passée'
              : 'vente enregistrée'}{' '}
          hors ligne. Le numéro définitif sera attribué au retour de la connexion.
        </Box>
      )}

      {vente.status === 'cancelled' && (
        <Box sx={{ marginTop: '10px', fontWeight: 700, textAlign: 'center' }}>
          {vente.docType === 'quote'
            ? '*** DEVIS REFUSÉ ***'
            : vente.docType === 'order'
              ? '*** COMMANDE ANNULÉE ***'
              : '*** VENTE ANNULÉE ***'}
        </Box>
      )}

      <Box sx={{ marginTop: '12px', textAlign: 'center' }}>
        {vente.docType === 'quote'
          ? 'Devis sans engagement — les prix sont donnés sous réserve de disponibilité.'
          : vente.docType === 'order'
            ? 'Commande enregistrée — nous vous informerons dès qu’elle sera prête.'
            : 'Merci de votre visite'}
      </Box>
      <Box sx={{ marginTop: '2px', textAlign: 'center', fontSize: '9px' }}>
        {boutique?.name} — propulsé par eduqor
      </Box>
    </Box>
  )
}
